import { forwardRef, type CSSProperties, type ReactNode } from "react";
import { Loader2, Lock, MapPin } from "lucide-react";
import { getProductImageUrl } from "../../utils/imageUtils";
import { formatDealPrice, type OnbDeal } from "../../utils/onboardingCoverage";
import { useTranslation } from "../../context/LanguageContext";
import bulb from "../../assets/onboarding/products/bulb.jpg";
import creamer from "../../assets/onboarding/products/creamer.jpg";
import paddle from "../../assets/onboarding/products/paddle.jpg";
import soda from "../../assets/onboarding/products/soda.jpg";
import tumbler from "../../assets/onboarding/products/tumbler.jpg";

/**
 * Shared pieces of the "deals first" onboarding (ZIP → plans → not covered).
 * Colours are the --onb-* variables in src/index.css, so the pages follow the
 * site theme (html.dark); the yellow CTA is the same in both themes.
 */

export const ONB_FONT = "Outfit, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
export const ONB_GRADIENT = "linear-gradient(135deg,#5B21D6 0%,#7C3AED 38%,#B4378F 72%,#E59A3A 100%)";
export const ONB_YELLOW = "#FFD24A";
export const ONB_YELLOW_INK = "#2A0A5E";
export const ONB_YELLOW_EDGE = "#E0B021";
export const ONB_RED = "#E5322D";
/** App Store average shown on the landing page (LandingPage APP_RATING); keep the two in step. */
export const APP_STORE_RATING = "4.8";

export const C = {
  page: "var(--onb-page)",
  card: "var(--onb-card)",
  ink: "var(--onb-ink)",
  muted: "var(--onb-muted)",
  soft: "var(--onb-soft)",
  body: "var(--onb-body)",
  line: "var(--onb-line)",
  field: "var(--onb-field)",
  fieldBorder: "var(--onb-field-border)",
  sel: "var(--onb-sel)",
  selBorder: "var(--onb-sel-border)",
  green: "var(--onb-green)",
  greenBg: "var(--onb-green-bg)",
  chip: "var(--onb-chip)",
  icon: "var(--onb-icon)",
  iconBg: "var(--onb-icon-bg)",
  tile: "var(--onb-tile)",
  shadow: "var(--onb-shadow)",
  glow: "var(--onb-glow)",
  skeleton: "var(--onb-skeleton)",
  inkBtn: "var(--onb-ink-btn)",
  onInkBtn: "var(--onb-on-ink-btn)",
  errorBg: "var(--onb-error-bg)",
  errorInk: "var(--onb-error-ink)",
} as const;

/** Product photos bundled for when no real deals could be loaded (no prices shown for these). */
export const FALLBACK_PRODUCT_IMAGES = [bulb, creamer, paddle, soda, tumbler];

/** Full-height themed page in the onboarding font (pages also call usePageBackground(C.page)). */
export function OnbPage({ children, testId, className = "" }: { children: ReactNode; testId?: string; className?: string }) {
  return (
    <div
      data-testid={testId}
      className={`relative min-h-[100svh] w-full overflow-x-hidden ${className}`}
      style={{ backgroundColor: C.page, color: C.ink, fontFamily: ONB_FONT }}
    >
      {children}
    </div>
  );
}

export function OnbLogo({ className = "h-[30px]" }: { className?: string }) {
  return (
    <>
      <img src="/logo_main.svg" alt="DealHawk" className={`${className} w-auto dark:hidden`} />
      <img src="/logo_main_white.svg" alt="DealHawk" className={`${className} hidden w-auto dark:block`} />
    </>
  );
}

export function OnbTopBar({ onSignOut, left }: { onSignOut: () => void; left?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <header className="relative z-20 mx-auto flex w-full max-w-[1280px] items-center justify-between px-5 pt-5 lg:px-10 lg:pt-7">
      <div className="flex items-center gap-2">
        {left}
        <OnbLogo className="h-[26px] lg:h-[30px]" />
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="rounded-lg px-1 py-1 text-[14px] font-semibold transition hover:underline lg:text-[15px]"
        style={{ color: C.muted }}
      >
        {t("onboarding2.sign_out")}
      </button>
    </header>
  );
}

export function YellowCta({
  children,
  onClick,
  disabled,
  busy,
  height = 56,
  fontSize = 17,
  className = "",
  ariaLabel,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  busy?: boolean;
  height?: number;
  fontSize?: number;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <button
      type={onClick ? "button" : "submit"}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      className={`group flex w-full items-center justify-center gap-2 rounded-full font-extrabold transition-[transform,box-shadow] duration-100 hover:brightness-[1.03] active:translate-y-[3px] active:shadow-[0_2px_0_#E0B021] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:translate-y-0 ${className}`}
      style={{ height, fontSize, backgroundColor: ONB_YELLOW, color: ONB_YELLOW_INK, boxShadow: `0 5px 0 ${ONB_YELLOW_EDGE}` }}
    >
      {busy ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="sr-only">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

const STAR_PATH = "M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z";

export function RatingLine({ size = 14 }: { size?: number }) {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-[3px]">
      {Array.from({ length: 5 }, (_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill="#F5B400" aria-hidden="true">
          <path d={STAR_PATH} />
        </svg>
      ))}
      <span className="ml-1.5 font-bold" style={{ fontSize: size, color: C.muted }}>
        {t("onboarding2.dz_rating", { rating: APP_STORE_RATING })}
      </span>
    </span>
  );
}

export function LanguageToggle({
  value,
  onChange,
  label,
}: {
  value: "en" | "es";
  onChange: (lang: "en" | "es") => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex gap-1 rounded-xl p-1" style={{ backgroundColor: C.chip }}>
      {(["en", "es"] as const).map((lang) => {
        const active = value === lang;
        return (
          <button
            key={lang}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(lang)}
            className="flex h-9 flex-1 items-center justify-center rounded-[9px] text-[14px] font-bold transition"
            style={{
              backgroundColor: active ? C.card : "transparent",
              color: active ? C.ink : C.muted,
              boxShadow: active ? "0 1px 3px rgba(0,0,0,.12)" : undefined,
            }}
          >
            {lang === "en" ? "English" : "Español"}
          </button>
        );
      })}
    </div>
  );
}

export const ZipField = forwardRef<HTMLInputElement, {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  invalid?: boolean;
  autoFocus?: boolean;
  className?: string;
}>(function ZipField({ value, onChange, placeholder, label, invalid, autoFocus, className = "" }, ref) {
  return (
    <label
      className={`flex h-[60px] min-w-0 items-center gap-2.5 rounded-2xl border-2 px-[18px] transition focus-within:border-[#7C3AED] focus-within:ring-4 focus-within:ring-[#7C3AED]/15 ${className}`}
      style={{ backgroundColor: C.field, borderColor: invalid ? "#E5322D" : C.fieldBorder }}
    >
      <MapPin size={20} aria-hidden="true" style={{ color: C.soft, flexShrink: 0 }} />
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="postal-code"
        aria-label={label}
        aria-invalid={invalid || undefined}
        maxLength={5}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
        placeholder={placeholder}
        className="h-full w-full min-w-0 bg-transparent text-[22px] font-bold tracking-[2px] outline-none placeholder:font-semibold placeholder:tracking-normal placeholder:text-[var(--onb-soft)]"
        style={{ color: C.ink }}
      />
    </label>
  );
});

export function OffBadge({ pct, className = "", style }: { pct: number; className?: string; style?: CSSProperties }) {
  return (
    <span
      className={`absolute flex h-[22px] items-center rounded-[7px] px-2 text-[12px] font-black text-white ${className}`}
      style={{ backgroundColor: ONB_RED, ...style }}
    >
      -{Math.round(pct)}%
    </span>
  );
}

export function ProductImage({ src, className = "", inset = "15%" }: { src: string; className?: string; inset?: string }) {
  const resolved = src.startsWith("http") ? getProductImageUrl(src, "300") : src;
  return (
    <img
      src={resolved}
      alt=""
      loading="lazy"
      className={`absolute object-contain mix-blend-multiply ${className}`}
      style={{ left: inset, top: inset, width: `calc(100% - 2 * ${inset})`, height: `calc(100% - 2 * ${inset})` }}
    />
  );
}

export function MembersPricePill() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex h-6 items-center gap-[5px] rounded-[7px] px-[9px] text-[12px] font-extrabold" style={{ backgroundColor: C.chip, color: C.muted }}>
      <Lock size={12} strokeWidth={2.4} aria-hidden="true" />
      {t("onboarding2.dp_members_price")}
    </span>
  );
}

/** Feed-style card: photo tile with the % badge, price (or the members lock), name, store. */
export function DealGridCard({ deal, locked = false }: { deal: OnbDeal; locked?: boolean }) {
  const { t } = useTranslation();
  const shelf = locked
    ? ""
    : [deal.aisle ? t("onboarding2.dp_aisle", { aisle: deal.aisle }) : "", deal.quantity ? t("onboarding2.dp_left", { count: deal.quantity }) : ""]
        .filter(Boolean)
        .join(" · ");
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="relative aspect-square overflow-hidden rounded-2xl" style={{ backgroundColor: C.tile }}>
        <ProductImage src={getProductImageUrl(deal.image, "400")} />
        <OffBadge pct={deal.discountPercent} className="bottom-2.5 left-2.5" />
      </div>
      <div className="flex min-h-[26px] items-baseline gap-1.5">
        {locked ? (
          <MembersPricePill />
        ) : (
          <>
            <span className="text-[19px] font-black leading-6" style={{ color: C.green }}>{formatDealPrice(deal.price)}</span>
            <s className="truncate text-[12px]" style={{ color: C.soft }}>{formatDealPrice(deal.wasPrice)}</s>
          </>
        )}
      </div>
      <span className="line-clamp-2 text-[13px] leading-[17px]" style={{ color: C.body }}>{deal.name}</span>
      {shelf ? (
        <span className="truncate text-[12px] font-extrabold" style={{ color: C.green }}>{shelf}</span>
      ) : null}
      {deal.storeLabel ? (
        <span className="truncate text-[11.5px] font-bold" style={{ color: C.icon }}>{deal.storeLabel}</span>
      ) : null}
    </div>
  );
}

export function DealGridSkeleton({ count, columns }: { count: number; columns: string }) {
  return (
    <div className={`grid ${columns}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="aspect-square animate-pulse rounded-2xl" style={{ backgroundColor: C.skeleton }} />
          <div className="h-5 w-2/3 animate-pulse rounded-md" style={{ backgroundColor: C.skeleton }} />
          <div className="h-3.5 w-full animate-pulse rounded-md" style={{ backgroundColor: C.skeleton }} />
        </div>
      ))}
    </div>
  );
}

export function GreenPill({ children, small = false }: { children: ReactNode; small?: boolean }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full font-extrabold ${small ? "h-6 px-[9px] text-[11.5px]" : "h-[26px] px-[11px] text-[12.5px]"}`}
      style={{ backgroundColor: C.greenBg, color: C.green }}
    >
      {children}
    </span>
  );
}

export function OnbError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-[14px] px-3 py-2.5 text-center text-[13px] font-semibold" style={{ backgroundColor: C.errorBg, color: C.errorInk }}>
      {error}
    </div>
  );
}
